import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeartHandshake } from "lucide-react";
import dayjs from "dayjs";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import { EMAIL_LEAD_WELCOME_REPORT_URL, USERS_SIMPLE_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { Stat, inputCls, minutesLabel } from "../../components/assignment/shared";

// What each engagement signal is called on a lead in the "act now" list.
const SIGNAL_LABEL = {
    email_opened: "Opened email", email_clicked: "Clicked email", wa_read: "Read WhatsApp",
    wa_replied: "Replied", page_opened: "Opened details page", confirmed: "Confirmed details",
    verified: "Verified", slots: "Picked demo slots",
};

// The funnel in stages, so the eye reads welcome -> engagement -> sales.
const STAGES = [
    { title: "Welcome sent", keys: ["new", "email_sent", "wa_sent"] },
    { title: "Engaged", keys: ["email_opened", "email_clicked", "wa_delivered", "wa_read", "wa_replied"] },
    { title: "Confirmed", keys: ["page_opened", "confirmed", "verified", "slots"] },
    { title: "Sales", keys: ["called", "demo", "won"] },
];

/**
 * Reports -> Lead Welcome & Engagement. What happened to each new lead after
 * it arrived: welcomed by email / WhatsApp, engaged, confirmed, called - and
 * the leads who engaged but nobody has called yet.
 * email/lead-welcome/report/ (platform_core/email_integration/welcome_report.py).
 */
export default function LeadWelcomeReport() {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();
    const [range, setRange] = useState({
        from: dayjs().subtract(29, "day").format("YYYY-MM-DD"),
        to: dayjs().format("YYYY-MM-DD"),
        origin: "",
        owner_id: "",
    });
    const [users, setUsers] = useState([]);
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        fetch(USERS_SIMPLE_URL).then(res => {
            const rows = res?.data || res?.results || (Array.isArray(res) ? res : []);
            setUsers(Array.isArray(rows) ? rows : []);
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        setData(null);
        const params = Object.fromEntries(Object.entries(range).filter(([, v]) => v));
        fetch(EMAIL_LEAD_WELCOME_REPORT_URL, params).then(res => {
            if (res?.status === "success") { setData(res.data); setError(""); }
            else setError(res?.message || "Could not load the report");
        });
    }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

    const s = data?.summary || {};
    const funnel = Object.fromEntries((data?.funnel || []).map(f => [f.key, f]));
    const openLead = (id) => navigate(`/customers/customerdetails?id=${id}`,
        { state: { formEdit: true, row: { id } } });

    return (
        <ContainerCard>
            <div className="p-4 space-y-5 overflow-auto">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <HeartHandshake className="h-5 w-5 text-primary" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Lead Welcome & Engagement</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                New leads from welcome message to first call — who engaged, who confirmed, and who is still waiting for a call.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <input type="date" value={range.from} aria-label="From"
                            onChange={e => setRange(r => ({ ...r, from: e.target.value }))} className={cn(inputCls, "w-36")} />
                        <input type="date" value={range.to} aria-label="To"
                            onChange={e => setRange(r => ({ ...r, to: e.target.value }))} className={cn(inputCls, "w-36")} />
                        <select value={range.origin} aria-label="Source" onChange={e => setRange(r => ({ ...r, origin: e.target.value }))}
                            className={cn(inputCls, "w-32")}>
                            <option value="">All sources</option>
                            <option value="Meta">Meta</option>
                            <option value="Website">Website</option>
                            <option value="Manual">Manual</option>
                        </select>
                        <select value={range.owner_id} aria-label="Rep" onChange={e => setRange(r => ({ ...r, owner_id: e.target.value }))}
                            className={cn(inputCls, "w-40")}>
                            <option value="">All reps</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>
                                    {[u.login_fname, u.login_lname].filter(Boolean).join(" ") || u.login_name || u.name || `User ${u.id}`}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {error && <p className="text-xs text-gray-500">{error}</p>}
                {!data && !error && <p className="text-xs text-gray-400">Loading…</p>}

                {data && (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                            <Stat label="New leads" value={s.leads} />
                            <Stat label="Welcomed" value={pct(s.reached, s.leads)} hint={`${s.reached} got an email or WhatsApp`} />
                            <Stat label="Engaged" value={pct(s.engaged, s.leads)} hint={`${s.engaged} opened, read, replied or clicked`} />
                            <Stat label="Confirmed details" value={pct(s.confirmed, s.leads)} hint={`${s.verified} verified a contact`} />
                            <Stat label="Time to first call (median)" value={minutesLabel(s.median_minutes_to_call)}
                                hint={`${s.called_within_1h_pct}% called within an hour`} />
                            <Stat label="Engaged, not called yet" value={s.engaged_not_called}
                                tone={s.engaged_not_called ? "warn" : undefined} hint="Call these first — list below" />
                        </div>

                        <section>
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">The journey</h3>
                            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                                {STAGES.map(stage => (
                                    <div key={stage.title} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">{stage.title}</p>
                                        <div className="space-y-1.5">
                                            {stage.keys.map(k => funnel[k] && (
                                                <FunnelRow key={k} label={funnel[k].label} count={funnel[k].count}
                                                    pctOfAll={funnel[k].pct} />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section>
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Engaged, not called yet</h3>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                                These leads opened, read, replied or confirmed — and nobody has logged a call. Newest first.
                            </p>
                            {data.act_now.length === 0 ? (
                                <p className="text-xs text-gray-400">Every engaged lead has been called. 👍</p>
                            ) : (
                                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                    <table className="min-w-full text-xs">
                                        <thead className="bg-gray-50 dark:bg-gray-700/40 text-gray-500">
                                            <tr>{["Lead", "Source", "Owner", "Stage", "What they did", "Arrived"].map(c =>
                                                <th key={c} className="px-3 py-2 text-left font-medium">{c}</th>)}</tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {data.act_now.map(r => (
                                                <tr key={r.id}>
                                                    <td className="px-3 py-2">
                                                        <button type="button" onClick={() => openLead(r.id)}
                                                            className="text-brand-600 dark:text-brand-300 hover:underline text-left">
                                                            {r.customer_name || `Lead #${r.id}`}
                                                        </button>
                                                        {r.contact_name && <span className="block text-[10px] text-gray-400">{r.contact_name}</span>}
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{r.origin}</td>
                                                    <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{r.owner || "Unassigned"}</td>
                                                    <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{r.lead_stage || "—"}</td>
                                                    <td className="px-3 py-2">
                                                        <div className="flex flex-wrap gap-1">
                                                            {r.signals.map(k => (
                                                                <span key={k} className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 text-[10px]">
                                                                    {SIGNAL_LABEL[k] || k}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{dayjs(r.created_date).format("D MMM, h:mm A")}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        <section>
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">New leads, welcomed and engaged, per day</h3>
                            <DayBars days={dailySeries(data.range.from, data.range.to, data.daily)} />
                        </section>

                        <div className="grid lg:grid-cols-2 gap-5">
                            <section>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">By source</h3>
                                <SimpleTable cols={["Source", "Leads", "Engaged", "Confirmed", "Reached demo", "First call (median)"]}
                                    rows={data.by_origin.map(o => [o.origin, o.leads, `${o.engaged} (${o.engaged_pct}%)`,
                                        `${o.confirmed} (${o.confirmed_pct}%)`, `${o.demo} (${o.demo_pct}%)`,
                                        minutesLabel(o.median_minutes_to_call)])}
                                    empty="No new leads in this period" />
                            </section>
                            <section>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Why a welcome was not sent</h3>
                                <SimpleTable cols={["Channel", "Reason", "Leads"]}
                                    rows={data.not_sent.map(r => [r.channel, r.reason, r.count])}
                                    empty="Every new lead was welcomed (or the welcome is switched off)" />
                            </section>
                        </div>

                        <section>
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">By rep</h3>
                            <SimpleTable cols={["Rep", "Leads", "Engaged", "Called", "Engaged, not called", "Reached demo", "First call (median)", "Called within 1 h"]}
                                rows={data.by_rep.map(r => [r.owner, r.leads, r.engaged, r.called,
                                    r.engaged_not_called ? <span className="font-semibold text-amber-600">{r.engaged_not_called}</span> : 0,
                                    r.demo, minutesLabel(r.median_minutes_to_call), `${r.called_within_1h_pct}%`])}
                                empty="No new leads in this period" />
                        </section>
                    </>
                )}
            </div>
        </ContainerCard>
    );
}

const pct = (part, whole) => (whole ? `${Math.round((100 * part) / whole)}%` : "—");

function FunnelRow({ label, count, pctOfAll }) {
    return (
        <div className="text-xs" title={`${count} lead(s), ${pctOfAll}% of new leads`}>
            <div className="flex justify-between gap-2">
                <span className="text-gray-700 dark:text-gray-200 truncate">{label}</span>
                <span className="tabular-nums text-gray-900 dark:text-white">{count} <span className="text-gray-400">· {pctOfAll}%</span></span>
            </div>
            <div className="h-1.5 mt-0.5 bg-gray-100 dark:bg-gray-700/50 rounded">
                <div className="h-1.5 bg-primary rounded-r-[4px]" style={{ width: `${Math.min(100, pctOfAll)}%` }} />
            </div>
        </div>
    );
}

function dailySeries(from, to, rows) {
    const byDay = Object.fromEntries((rows || []).map(r => [r.date, r]));
    const out = [];
    for (let d = dayjs(from); !d.isAfter(dayjs(to)); d = d.add(1, "day")) {
        const key = d.format("YYYY-MM-DD");
        const r = byDay[key] || {};
        out.push({ key, label: d.format("D MMM"), new: r.new || 0, reached: r.reached || 0, engaged: r.engaged || 0 });
    }
    return out;
}

// Three bars per day, side by side: new, welcomed, engaged.
function DayBars({ days }) {
    const max = Math.max(1, ...days.map(d => d.new));
    if (!days.some(d => d.new)) return <p className="text-xs text-gray-400 py-4">No new leads in this period.</p>;
    const series = [
        { key: "new", label: "New", cls: "bg-gray-300 dark:bg-gray-600" },
        { key: "reached", label: "Welcomed", cls: "bg-primary/60" },
        { key: "engaged", label: "Engaged", cls: "bg-primary" },
    ];
    return (
        <div>
            <div className="flex gap-3 text-[10px] text-gray-500 mb-1">
                {series.map(s => <span key={s.key} className="flex items-center gap-1"><span className={cn("w-2.5 h-2.5 rounded-sm", s.cls)} />{s.label}</span>)}
            </div>
            <div className="flex items-end gap-1 h-32 border-b border-gray-200 dark:border-gray-700" role="img"
                aria-label="New, welcomed and engaged leads per day">
                {days.map(d => (
                    <div key={d.key} className="flex-1 h-full flex items-end gap-px"
                        title={`${d.label}: ${d.new} new, ${d.reached} welcomed, ${d.engaged} engaged`}>
                        {series.map(s => (
                            <div key={s.key} className={cn("flex-1 rounded-t-[2px]", s.cls)}
                                style={{ height: `${(d[s.key] / max) * 100}%`, minHeight: d[s.key] ? 2 : 0 }} />
                        ))}
                    </div>
                ))}
            </div>
            <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                <span>{days[0].label}</span><span>max {max} a day</span><span>{days[days.length - 1].label}</span>
            </div>
        </div>
    );
}

function SimpleTable({ cols, rows, empty }) {
    if (!rows.length) return <p className="text-xs text-gray-400">{empty}</p>;
    return (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-700/40 text-gray-500">
                    <tr>{cols.map(c => <th key={c} className="px-3 py-2 text-left font-medium">{c}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {rows.map((r, i) => <tr key={i}>{r.map((c, j) =>
                        <td key={j} className={cn("px-3 py-2", j ? "tabular-nums text-gray-700 dark:text-gray-200" : "text-gray-800 dark:text-gray-100")}>{c}</td>)}</tr>)}
                </tbody>
            </table>
        </div>
    );
}
